import React, { useState, useEffect } from 'react';
import dayjs from 'dayjs';
import {
  Box,
  TextField,
  Button,
  Typography,
  IconButton,
  CircularProgress,
  Autocomplete,
  Divider
} from '@mui/material';
import { Save, Send, UserPen, Trash, CirclePlus } from 'lucide-react';
import { FilterFieldSx, TextFeaildSx, DropDownSX } from '../../../constants/style';
// import { saveDCR } from '../../../apis/DCR';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { fetchDcrClients } from '../../../apis/DCR/Clients';
import Tooltip from '../../../utils/Tooltip';
import CommonDialog from '../common/CommonDialog';
import AddClient from '../Clients/AddClient';
import { fetchDcrListById } from '../../../apis/DCR/List';
import { fetchDcrClientsById } from '../../../apis/DCR/Clients';
import { saveDcrList, deleteDcrList } from '../../../apis/DCR/List';
import { NumericFormat } from 'react-number-format';
import { userScreenAccess, formatDateTime } from '../../../utils';
import ConfirmDialog from '../common/ConfirmDialog';
import ChooseClient from '../common/ChooseClient';

const CALL_TYPE_OPTIONS = [
  { value: 'Field Visit', label: 'Field Visit' },
  { value: 'Follow Up', label: 'Follow Up' },
  { value: 'Demo', label: 'Demo' },
  { value: 'Closure', label: 'Closure' }
];

const USER_TYPE_OPTIONS = [
  { value: 'User', label: 'User' },
  { value: 'Non User', label: 'Non User' }
];

const emptyForm = {
  name: '',
  address: '',
  repVisitingHours: '',
  date: dayjs().format('YYYY-MM-DD HH:mm:ss'),
  visitedPerson: '',
  contacts: ['', '', '', ''],
  contactNo: 0,
  softwareNameGUID: 0,
  userTypeGUID: 0,
  callTypeGUID: 0,
  location: '',
  remarks: '',
  clientGUID: 0,
  clientName: '',
  contactPersonName1: '',
  contactPersonName2: '',
  contactPersonName3: '',
  contactPersonName4: '',
  contactPersonDesignation1: '',
  contactPersonDesignation2: '',
  contactPersonDesignation3: '',
  contactPersonDesignation4: '',
  contactNo1: '',
  contactNo2: '',
  contactNo3: '',
  contactNo4: '',
  address1: '',
  address2: '',
  area: '',
  city: '',
  installationID: '',
  modules: '',
  installationDate: null,
  installationDateString: '',
  billAmount: 0,
  amcAmount: 0,
  userGUID: 0,
  createdGUID: 0,
  createdDate: '2026-04-21',
  modifiedGUID: 0,
  modifiedDate: '2026-04-21',
  versionNo: '',
  modulesList: [],
  isActive: true,
  address: '',
  contact1: '',
  contact2: '',
  contact3: '',
  contact4: '',
  dailyCallClientGUID: 0,
  dailyCallClientName: '',
  dailyCallCityGUID: 0,
  dailyCallCityName: '',
  dailyCallAreaGUID: 0,
  dailyCallAreaName: '',
  dailyCallGUID: 0,
  visitingDate: dayjs(),
  visitingDateString: '',
  visitingTime: '',
  contactPersonName: ''
};

const AddDcr = ({
  userDetails = null,
  dcrRecord = null,
  clientsOptions = [],
  inChargeOptions = [],
  softwareOptions = [],
  userNonUserOptions = [],
  callTypeOptions = [],
  userAccess = null,
  onSuccess,
  onCancel
}) => {
  const isEditMode = Boolean(dcrRecord);
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [saving, setSaving] = useState(false);
  const [clientDetailsLoading, setClientDetailsLoading] = useState(false);
  const [clients, setClients] = useState(clientsOptions || []);
  const [clientLoading, setClientLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [deleting, setDeleting] = useState(false);
  // const isUser = userDetails?.role == AuthConstant.USER;
  const isDeleteAllowd = isEditMode ? userAccess?.hasDeletePermission : false;
  const isEditAllowed = isEditMode ? userAccess?.hasEditPermission : userAccess?.hasAddPermission;
  const isClientPermission = userScreenAccess('/dcr-clients');

  // ── Confirm-dialog state ────────────────────────────────────────────────
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: '',
    content: '',
    onConfirm: null
  });

  const openConfirm = ({ title, content, onConfirm }) =>
    setConfirmDialog({ open: true, title, content, onConfirm });

  const closeConfirm = () =>
    setConfirmDialog((prev) => ({ ...prev, open: false, onConfirm: null }));

  const deleteHandler = () => {
    if (!userAccess?.hasDeletePermission) return;
    openConfirm({
      title: 'Delete Installation Record',
      content: `Are you sure you want to delete the Installation Record? This action cannot be undone.`,
      onConfirm: () => {
        closeConfirm();
        handleDelete();
      }
    });
  };

  const getDcrListById = async () => {
    try {
      const response = await fetchDcrListById(dcrRecord.dailyCallGUID);
      if (response.success) {
        setForm((prev) => ({ ...prev, ...response.data }));
        updateClientDetails(response?.data?.dailyCallClientGUID);
      }
    } catch (error) {
      console.log('Error fetching DCR list by ID:', error);
    }
  };

  useEffect(() => {
    if (dcrRecord) {
      getDcrListById();
    }
    // if (isUser) {
    //   setForm((prev) => ({ ...prev, userGUID: userDetails?.userGUID }));
    // }
    if (clientsOptions.length === 0) {
      fetchClientListResponse();
    }
  }, []);

  const handleChange = (field) => (event) => {
    const value = event?.target?.value ?? event;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleBlur = (field) => () => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    const errs = validate();
    setErrors((prev) => ({ ...prev, [field]: errs[field] }));
  };

  const validate = () => {
    const nextErrors = {};
    if (!form.dailyCallClientGUID) nextErrors.dailyCallClientGUID = 'Client Name is required.';
    if (!form.visitingTime.trim()) nextErrors.visitingTime = 'Rep Visiting Hours is required.';
    if (!form.contactPersonName.trim())
      nextErrors.contactPersonName = 'Visited Person is required.';
    if (!form.callTypeGUID) nextErrors.callTypeGUID = 'Call Type is required.';
    if (!form.softwareUsageTypeGUID)
      nextErrors.softwareUsageTypeGUID = 'User / Non User is required.';
    if (!form.softwareNameGUID) nextErrors.softwareNameGUID = 'Software Name is required.';
    if (!form.remarks.trim()) nextErrors.remarks = 'Remarks are required.';

    return nextErrors;
  };

  const handleSave = async () => {
    const allTouched = {
      dailyCallClientGUID: true,
      visitingTime: true,
      contactPersonName: true,
      contactNo: true,
      userGUID: true,
      visitingDate: true,
      callTypeGUID: true,
      softwareUsageTypeGUID: true,
      softwareNameGUID: true,
      remarks: true
    };
    setTouched(allTouched);
    const nextErrors = validate();
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;

    setSaving(true);

    const payload = {
      ...form,
      visitingDate: formatDateTime(form.visitingDate)
    }

    const successMsg = isEditMode ? 'DCR updated successfully' : 'DCR added successfully';
    const response = await saveDcrList(payload, successMsg, '');
    setSaving(false);
    if (response.success) {
      onSuccess?.();
    }
  };

  const editClientDetails = async () => {
    if (!form.dailyCallClientGUID) return null;
    updateClientDetails(form.dailyCallClientGUID);
  };

  const fetchClientListResponse = async () => {
    setClientLoading(true);
    try {
      const response = await fetchDcrClients({ statusGUID: 1 });
      setClients(response || []);
    } catch (err) {
      console.log('Error fetching client list:', err);
    } finally {
      setClientLoading(false);
    }
  };

  const handleClientChange = async (dailyCallClientGUID) => {
    setForm((prev) => ({ ...prev, dailyCallClientGUID }));
    updateClientDetails(dailyCallClientGUID);
  };

  const updateClientDetails = async (dailyCallClientGUID) => {
    const clientData =
      dailyCallClientGUID > 0 ? await fetchDcrClientsById(dailyCallClientGUID) : {};
    const next = {
      visitingTime: clientData?.visitingTime || '',
      address1: clientData?.address1 || '',
      address2: clientData?.address2 || '',
      contactPersonName1: clientData?.contactPersonName1 || '',
      contactPersonDesignation1: clientData?.contactPersonDesignation1 || '',
      contactNo1: clientData?.contactNo1 || '',
      contactPersonName2: clientData?.contactPersonName2 || '',
      contactPersonDesignation2: clientData?.contactPersonDesignation2 || '',
      contactNo2: clientData?.contactNo2 || '',
      contactPersonName3: clientData?.contactPersonName3 || '',
      contactPersonDesignation3: clientData?.contactPersonDesignation3 || '',
      contactNo3: clientData?.contactNo3 || '',
      contactPersonName4: clientData?.contactPersonName4 || '',
      contactPersonDesignation4: clientData?.contactPersonDesignation4 || '',
      contactNo4: clientData?.contactNo4 || '',
      address: clientData
        ? [clientData?.address1, clientData?.address2].filter(Boolean).join(' | ') + ' ' + [clientData?.dailyCallArea, clientData?.dailyCallCity].filter(Boolean).join(' , ')
        : '',
      contact1: clientData
        ? [
          clientData.contactPersonName1,
          clientData.contactPersonDesignation1,
          clientData.contactNo1
        ]
          .filter(Boolean)
          .join(' | ')
        : '',

      contact2: clientData
        ? [
          clientData.contactPersonName2,
          clientData.contactPersonDesignation2,
          clientData.contactNo2
        ]
          .filter(Boolean)
          .join(' | ')
        : '',

      contact3: clientData
        ? [
          clientData.contactPersonName3,
          clientData.contactPersonDesignation3,
          clientData.contactNo3
        ]
          .filter(Boolean)
          .join(' | ')
        : '',

      contact4: clientData
        ? [
          clientData.contactPersonName4,
          clientData.contactPersonDesignation4,
          clientData.contactNo4
        ]
          .filter(Boolean)
          .join(' | ')
        : ''
    };
    setForm((prev) => ({ ...prev, ...next }));
  };

  const handleInChargeChange = (event, value) => {
    const userGUID = value?.userGUID || 0;
    const next = { ...form, userGUID };
    setForm(next);
  };

  const handleUserTypeChange = (event, value) => {
    const softwareUsageTypeGUID = value?.globalTypeID || 0;
    const next = { ...form, softwareUsageTypeGUID };
    setForm(next);
  };

  const handleSoftwareNameChange = (event, value) => {
    const softwareNameGUID = value?.softwareNameGUID || 0;
    const next = { ...form, softwareNameGUID };
    setForm(next);
  };

  const handleCallTypeChange = (event, value) => {
    const callTypeGUID = value?.globalTypeID || 0;
    const next = { ...form, callTypeGUID };
    setForm(next);
  };

  const handleClickAddClient = () => {
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      const successMsg = `DCR deleted successfully`;
      const response = await deleteDcrList(form?.dailyCallGUID, successMsg, '');
      if (response && response.success) {
        onSuccess?.();
      }
    } catch (err) {
      console.log('Failed to delete global type:', err);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <Box sx={{ mt: 1 }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: 3,
            alignItems: 'stretch'
          }}
        >
          {clientDetailsLoading && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(255, 255, 255, 0.7)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1000,
                borderRadius: 1
              }}
            >
              <CircularProgress />
            </Box>
          )}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box
              sx={{
                p: 3,
                height: '100%',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 3,
                bgcolor: 'background.paper',
                boxShadow: '0 2px 10px rgba(0,0,0,0.04)'
              }}
            >
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: 2, mb: 2 }}>
                <Box
                  sx={{
                    gridColumn: { xs: 'span 12', md: 'span 6' },
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1
                  }}
                >
                  <Box sx={{ flex: 1 }}>
                    {/* <Autocomplete
                      size="small"
                      fullWidth
                      options={clients}
                      slotProps={{
                        paper: {
                          sx: DropDownSX
                        }
                      }}
                      value={
                        clients.find((c) => c.dailyCallClientGUID === form.dailyCallClientGUID) ||
                        null
                      }
                      onChange={handleClientChange}
                      disablePortal={false}
                      getOptionLabel={(option) => option?.dailyCallClientName || ''}
                      readOnly={isEditMode || !isEditAllowed}
                      isOptionEqualToValue={(option, value) =>
                        option?.dailyCallClientGUID === value?.dailyCallClientGUID
                      }
                      loading={clientLoading}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Client Name"
                          placeholder="Select Client"
                          sx={FilterFieldSx}
                          onBlur={handleBlur('clientName')}
                          error={touched.clientName && Boolean(errors.clientName)}
                          helperText={touched.clientName && errors.clientName}
                        />
                      )}
                    /> */}

                    <ChooseClient
                      clients={clients}
                      value={form.dailyCallClientGUID}
                      onChange={handleClientChange}
                      clientLoading={clientLoading}
                      onBlur={handleBlur('dailyCallClientGUID')}
                      error={touched.dailyCallClientGUID && Boolean(errors.dailyCallClientGUID)}
                      helperText={touched.dailyCallClientGUID && errors.dailyCallClientGUID}
                      isReadonly={isEditMode || !isEditAllowed}
                      isClientPermission={isClientPermission}
                      isEditMode={isEditMode}
                      fetchClientListResponse={fetchClientListResponse}
                      editClientDetails={editClientDetails}
                      isDcrClient={true}
                    />
                  </Box>
                  {/* {isClientPermission.hasEditPermission && isEditMode && (
                    <Tooltip title="Edit Client">
                      <IconButton
                        size="small"
                        onClick={() => handleClickAddClient()}
                        sx={{
                          p: 0.5,
                          color: 'text.secondary',
                          mt: 0.5
                        }}
                      >
                        <UserPen size={18} />
                      </IconButton>
                    </Tooltip>
                  )}

                  {isClientPermission.hasAddPermission && !isEditMode && (
                    <Tooltip title="Add Client">
                      <IconButton
                        size="small"
                        onClick={() => handleClickAddClient()}
                        sx={{
                          p: 0.5,
                          color: 'text.secondary',
                          mt: 0.5
                        }}
                      >
                        <CirclePlus size={18} />
                      </IconButton>
                    </Tooltip>
                  )} */}
                </Box>
                <Box sx={{ gridColumn: { xs: 'span 12', md: 'span 6' } }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Rep Visiting Hours"
                    value={form.visitingTime}
                    onChange={handleChange('visitingTime')}
                    onBlur={handleBlur('visitingTime')}
                    error={touched.visitingTime && Boolean(errors.visitingTime)}
                    helperText={touched.visitingTime && errors.visitingTime}
                    sx={FilterFieldSx}
                    slotProps={{ input: { readOnly: !isEditAllowed } }}
                  />
                </Box>
                <Box sx={{ gridColumn: 'span 12' }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Address"
                    multiline
                    maxRows={4}
                    value={form.address}
                    onChange={handleChange('address')}
                    slotProps={{ input: { readOnly: true } }}
                    sx={TextFeaildSx}
                  />
                </Box>
              </Box>
              {/* CONTACT */}
              <Typography sx={{ fontWeight: 600, mt: 3, mb: 2 }}>Contact Details</Typography>
              <Box sx={{ display: 'grid', gap: 2 }}>
                {form.contacts.map((contact, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2
                    }}
                  >
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Get person info"
                      label={`Person ${index + 1}`}
                      value={form[`contact${index + 1}`]}
                      multiline
                      maxRows={2}
                      inputProps={{
                        readOnly: true
                      }}
                      sx={TextFeaildSx}
                    />
                    <Button
                      variant="contained"
                      size="small"
                      onClick={() =>
                        console.log(`SMS functionality coming soon! ${form[`contact${index + 1}`]}`)
                      }
                      startIcon={<Send size={16} />}
                      sx={{ textTransform: 'none', borderRadius: 2, px: 2, flexShrink: 0 }}
                    >
                      SMS
                    </Button>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
          {/* RIGHT SIDE */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box
              sx={{
                p: 3,
                height: '100%',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 3,
                bgcolor: 'background.paper',
                boxShadow: '0 2px 10px rgba(0,0,0,0.04)'
              }}
            >
              <Typography sx={{ fontWeight: 600, mb: 2 }}>Visit Details</Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: 2 }}>
                <Box sx={{ gridColumn: { xs: 'span 12', md: 'span 6' } }}>
                  <Autocomplete
                    size="small"
                    fullWidth
                    options={inChargeOptions}
                    value={inChargeOptions.find((u) => u.userGUID === form.userGUID) || null}
                    onChange={handleInChargeChange}
                    disablePortal={false}
                    disableClearable={false}
                    getOptionLabel={(option) => option?.userName || ''}
                    isOptionEqualToValue={(option, value) => option?.userGUID === value?.userGUID}
                    readOnly={!isEditAllowed}
                    slotProps={{
                      paper: {
                        sx: DropDownSX
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="In Charge"
                        placeholder="Select In Charge"
                        sx={FilterFieldSx}
                      />
                    )}
                  />
                </Box>
                <Box sx={{ gridColumn: { xs: 'span 12', md: 'span 6' } }}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DateTimePicker
                      label="Date"
                      value={dayjs(form.visitingDate)} // default current date & time
                      format="DD/MM/YYYY hh:mm A" // display format
                      onChange={(newValue) =>
                        handleChange('visitingDate')({ target: { value: newValue } })
                      }
                      readOnly={!isEditAllowed}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          size: 'small',
                          onBlur: handleBlur('visitingDate'),
                          error: touched.visitingDate && Boolean(errors.visitingDate),
                          helperText: touched.visitingDate && errors.visitingDate,
                          sx: {
                            '& .MuiOutlinedInput-root': {
                              'min-height': 30
                            }
                          }
                        }
                      }}
                    />
                  </LocalizationProvider>
                </Box>
                <Box sx={{ gridColumn: { xs: 'span 12', md: 'span 6' } }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Visited Person"
                    value={form.contactPersonName}
                    onChange={handleChange('contactPersonName')}
                    onBlur={handleBlur('contactPersonName')}
                    error={touched.contactPersonName && Boolean(errors.contactPersonName)}
                    helperText={touched.contactPersonName && errors.contactPersonName}
                    sx={FilterFieldSx}
                    slotProps={{ input: { readOnly: !isEditAllowed } }}
                  />
                </Box>
                <Box sx={{ gridColumn: { xs: 'span 12', md: 'span 6' } }}>
                  <NumericFormat
                    value={form.contactNo}
                    onChange={handleChange('contactNo')}
                    onBlur={handleBlur('contactNo')}
                    customInput={TextField}
                    valueIsNumericString
                    variant="outlined"
                    label="Contact No "
                    fullWidth
                    size="small"
                    sx={FilterFieldSx}
                    isAllowed={(values) => values.value.length <= 10}
                    disabled={!isEditAllowed}
                  />
                </Box>
                <Box sx={{ gridColumn: { xs: 'span 12', md: 'span 4' } }}>
                  <Autocomplete
                    size="small"
                    fullWidth
                    options={callTypeOptions}
                    value={
                      callTypeOptions.find((u) => u.globalTypeID === form.callTypeGUID) || null
                    }
                    onChange={handleCallTypeChange}
                    disablePortal={false}
                    disableClearable={false}
                    getOptionLabel={(option) => option?.globalTypeName || ''}
                    isOptionEqualToValue={(option, value) =>
                      option?.globalTypeID === value?.globalTypeID
                    }
                    readOnly={!isEditAllowed}
                    slotProps={{
                      paper: {
                        sx: DropDownSX
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Call Type"
                        placeholder="Select Call Type"
                        sx={FilterFieldSx}
                      />
                    )}
                  />
                </Box>
                <Box sx={{ gridColumn: { xs: 'span 12', md: 'span 4' } }}>
                  <Autocomplete
                    size="small"
                    fullWidth
                    options={userNonUserOptions}
                    value={
                      userNonUserOptions.find(
                        (u) => u.globalTypeID === form.softwareUsageTypeGUID
                      ) || null
                    }
                    onChange={handleUserTypeChange}
                    disablePortal={false}
                    disableClearable={false}
                    getOptionLabel={(option) => option?.globalTypeName || ''}
                    isOptionEqualToValue={(option, value) =>
                      option?.globalTypeID === value?.softwareUsageTypeGUID
                    }
                    readOnly={!isEditAllowed}
                    slotProps={{
                      paper: {
                        sx: DropDownSX
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="User / Non User"
                        placeholder="Select User / Non User"
                        sx={FilterFieldSx}
                      />
                    )}
                  />
                </Box>
                <Box sx={{ gridColumn: { xs: 'span 12', md: 'span 4' } }}>
                  <Autocomplete
                    size="small"
                    fullWidth
                    options={softwareOptions}
                    value={
                      softwareOptions.find((u) => u.softwareNameGUID === form.softwareNameGUID) ||
                      null
                    }
                    onChange={handleSoftwareNameChange}
                    disablePortal={false}
                    disableClearable={false}
                    getOptionLabel={(option) => option?.softwareName || ''}
                    isOptionEqualToValue={(option, value) =>
                      option?.softwareNameGUID === value?.softwareNameGUID
                    }
                    readOnly={!isEditAllowed}
                    slotProps={{
                      paper: {
                        sx: DropDownSX
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Software"
                        placeholder="Select Software"
                        sx={FilterFieldSx}
                      />
                    )}
                  />
                </Box>
                <Box sx={{ gridColumn: 'span 12' }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Location"
                    multiline
                    maxRows={4}
                    value={form.location}
                    onChange={handleChange('location')}
                    slotProps={{ input: { readOnly: !isEditAllowed } }}
                    sx={TextFeaildSx}
                  />
                </Box>
                <Box sx={{ gridColumn: 'span 12' }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Remarks"
                    multiline
                    maxRows={4}
                    value={form.remarks}
                    onChange={handleChange('remarks')}
                    onBlur={handleBlur('remarks')}
                    error={touched.remarks && Boolean(errors.remarks)}
                    helperText={touched.remarks && errors.remarks}
                    slotProps={{ input: { readOnly: !isEditAllowed } }}
                    sx={TextFeaildSx}
                  />
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'flex-end', mt: 3 }}>
          <Button
            variant="contained"
            size="small"
            startIcon={
              deleting ? <CircularProgress size={14} color="inherit" /> : <Trash size={15} />
            }
            disabled={deleting}
            onClick={deleteHandler}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              minWidth: 100,
              background: '#e05757',
              '&:hover': { background: '#6d6464' },
              boxShadow: '0 4px 12px rgba(46,204,113,0.3)',
              display: !isEditMode || !isDeleteAllowd ? 'none' : 'inline-flex'
            }}
          >
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>

          <Button
            variant="contained"
            size="small"
            startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <Save size={15} />}
            disabled={saving || Object.keys(validate()).length > 0}
            onClick={handleSave}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              minWidth: 100,
              background: 'var(--primary, #36c95f)',
              '&:hover': { background: 'var(--primary-dark, #27ae60)' },
              boxShadow: '0 4px 12px rgba(46,204,113,0.3)',
              display: !isEditAllowed ? 'none' : 'inline-flex'
            }}
          >
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </Box>
      </Box>
      <CommonDialog
        open={openDialog}
        onClose={handleCloseDialog}
        title={isEditMode ? 'Edit Client' : 'Add Client'}
        persistent={true}
        maxWidth={'lg'}
        fullWidth={true}
      >
        <AddClient
          isDcrClient={true}
          client={isEditMode ? { dailyCallClientGUID: form.dailyCallClientGUID } : null}
          userAccess={isClientPermission}
          onSuccess={() => {
            handleCloseDialog();
            editClientDetails();
            fetchClientListResponse();
          }}
          onCancel={handleCloseDialog}
        />
      </CommonDialog>

      {/* ── Confirm Dialog ─────────────────────────────────────────────── */}
      <ConfirmDialog
        open={confirmDialog.open}
        onClose={closeConfirm}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        content={confirmDialog.content}
        titleIcon={<Trash size={18} />}
        confirmText="Delete"
        cancelText="Cancel"
        confirmColor="error"
      />
    </>
  );
};

export default AddDcr;
